/**
 * Unify Group Template model (HealthcareService with category group-template).
 *
 * Template owns: name, photo, comment; session HCS (auto-stamped Group Therapy catalog);
 * enrollment mode (`characteristic`); default capacity / modality (`serviceProvisionCode`) /
 * timezone; schedule defaults (sessionCount extension + `availableTime.daysOfWeek`).
 * Group screening is always required and uses the generic ServiceRequest screening stage,
 * so templates store no screening configuration.
 * Cohorts reference the template via GROUP_TEMPLATE_EXT.
 */
import type {
  Attachment,
  CodeableConcept,
  Extension,
  HealthcareService,
  Reference,
} from '@medplum/fhirtypes';

import {
  GROUP_ENROLLMENT_MODE_SYSTEM,
  GROUP_TEMPLATE_CAPACITY_EXT,
  GROUP_TEMPLATE_CATEGORY_CODE,
  GROUP_TEMPLATE_CATEGORY_SYSTEM,
  GROUP_TEMPLATE_SCHEDULE_DEFAULTS_EXT,
  GROUP_TEMPLATE_SESSION_HEALTHCARE_SERVICE_EXT,
  GROUP_TEMPLATE_TIMEZONE_EXT,
  SESSION_MODALITY_SYSTEM,
} from '../constants';
import type { GroupEnrollmentMode, GroupSessionModality } from './group';
import {
  DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME,
  DAY_OF_WEEK_CODES,
  type DayOfWeekCode,
  parseDayOfWeekCode,
} from './schedule';

export type GroupTemplateCapacity = {
  minEnrollment?: number;
  maxEnrollment?: number;
};

export type GroupTemplateScheduleDefaults = {
  /** Default "Appointments in series" (occurrence count) for new cohorts. */
  sessionCount?: number;
  /** Default meeting days as FHIR DaysOfWeek codes (`mon` … `sun`). */
  daysOfWeek?: DayOfWeekCode[];
};

function replaceExtension(
  svc: HealthcareService,
  url: string,
  next: Extension | undefined,
): HealthcareService {
  const others = (svc.extension ?? []).filter((e) => e.url !== url);
  const extension = next ? [...others, next] : others;
  return { ...svc, extension: extension.length ? extension : undefined };
}

function getRef(svc: HealthcareService, url: string): Reference | undefined {
  return svc.extension?.find((e) => e.url === url)?.valueReference;
}

function setRef(
  svc: HealthcareService,
  url: string,
  ref: Reference | undefined,
): HealthcareService {
  return replaceExtension(svc, url, ref ? { url, valueReference: ref } : undefined);
}

function subExt(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((e) => e.url === url);
}

function asGroupSessionModality(code: string | undefined): GroupSessionModality | undefined {
  return code === 'in-person' || code === 'telehealth' || code === 'hybrid' || code === 'either'
    ? code
    : undefined;
}

function asEnrollmentMode(code: string | undefined): GroupEnrollmentMode | undefined {
  return code === 'closed' || code === 'open' ? code : undefined;
}

function hasEnrollmentModeCoding(cc: CodeableConcept): boolean {
  return cc.coding?.some((c) => c.system === GROUP_ENROLLMENT_MODE_SYSTEM) ?? false;
}

function hasModalityCoding(cc: CodeableConcept): boolean {
  return cc.coding?.some((c) => c.system === SESSION_MODALITY_SYSTEM) ?? false;
}

/** Map FHIR DaysOfWeek codes to cohort `group-schedule` English weekday names. */
export function weekdayNamesFromDayOfWeekCodes(codes: DayOfWeekCode[]): string[] {
  return [...new Set(codes.map((c) => DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME[c]))];
}

export function isGroupTemplateHealthcareService(svc: HealthcareService): boolean {
  return (
    svc.category?.some((cat) =>
      cat.coding?.some(
        (c) => c.system === GROUP_TEMPLATE_CATEGORY_SYSTEM && c.code === GROUP_TEMPLATE_CATEGORY_CODE,
      ),
    ) ?? false
  );
}

export function withGroupTemplateCategory(svc: HealthcareService): HealthcareService {
  if (isGroupTemplateHealthcareService(svc)) return svc;
  return {
    ...svc,
    category: [
      ...(svc.category ?? []),
      {
        coding: [
          {
            system: GROUP_TEMPLATE_CATEGORY_SYSTEM,
            code: GROUP_TEMPLATE_CATEGORY_CODE,
            display: 'Group Template',
          },
        ],
      },
    ],
  };
}

export const getGroupTemplateSessionHealthcareService = (svc: HealthcareService) =>
  getRef(svc, GROUP_TEMPLATE_SESSION_HEALTHCARE_SERVICE_EXT);
export const setGroupTemplateSessionHealthcareService = (
  svc: HealthcareService,
  ref: Reference | undefined,
) => setRef(svc, GROUP_TEMPLATE_SESSION_HEALTHCARE_SERVICE_EXT, ref);

function getSessionCountFromExtension(svc: HealthcareService): number | undefined {
  const root = svc.extension?.find((e) => e.url === GROUP_TEMPLATE_SCHEDULE_DEFAULTS_EXT);
  const sessionCount =
    subExt(root?.extension, 'sessionCount')?.valueInteger
    ?? subExt(root?.extension, 'sessionCount')?.valueUnsignedInt;
  return sessionCount != null ? sessionCount : undefined;
}

function setSessionCountExtension(
  svc: HealthcareService,
  sessionCount: number | undefined,
): HealthcareService {
  const count = sessionCount != null && sessionCount > 0 ? sessionCount : undefined;
  return replaceExtension(
    svc,
    GROUP_TEMPLATE_SCHEDULE_DEFAULTS_EXT,
    count != null
      ? {
          url: GROUP_TEMPLATE_SCHEDULE_DEFAULTS_EXT,
          extension: [{ url: 'sessionCount', valueInteger: count }],
        }
      : undefined,
  );
}

export function getGroupTemplateDaysOfWeek(svc: HealthcareService): DayOfWeekCode[] {
  const days = (svc.availableTime ?? [])
    .flatMap((slot) => slot.daysOfWeek ?? [])
    .map((d) => parseDayOfWeekCode(d))
    .filter((d): d is DayOfWeekCode => d != null);
  return [...new Set(days)];
}

export function setGroupTemplateDaysOfWeek(
  svc: HealthcareService,
  daysOfWeek: DayOfWeekCode[] | undefined,
): HealthcareService {
  const unique = [
    ...new Set(
      (daysOfWeek ?? [])
        .map((d) => parseDayOfWeekCode(d))
        .filter((d): d is DayOfWeekCode => d != null),
    ),
  ].sort((a, b) => DAY_OF_WEEK_CODES.indexOf(a) - DAY_OF_WEEK_CODES.indexOf(b));
  return { ...svc, availableTime: unique.length ? [{ daysOfWeek: unique }] : undefined };
}

export function getGroupTemplateScheduleDefaults(
  svc: HealthcareService,
): GroupTemplateScheduleDefaults {
  const daysOfWeek = getGroupTemplateDaysOfWeek(svc);
  return {
    sessionCount: getSessionCountFromExtension(svc),
    daysOfWeek: daysOfWeek.length ? daysOfWeek : undefined,
  };
}

export function setGroupTemplateScheduleDefaults(
  svc: HealthcareService,
  defaults: GroupTemplateScheduleDefaults,
): HealthcareService {
  let next = setSessionCountExtension(svc, defaults.sessionCount);
  next = setGroupTemplateDaysOfWeek(next, defaults.daysOfWeek);
  return next;
}

export function getGroupTemplateSessionCount(svc: HealthcareService): number | undefined {
  return getSessionCountFromExtension(svc);
}

export function setGroupTemplateSessionCount(
  svc: HealthcareService,
  sessionCount: number | undefined,
): HealthcareService {
  return setSessionCountExtension(svc, sessionCount);
}

/** First default meeting day, if any. */
export function getGroupTemplateDayOfWeek(svc: HealthcareService): DayOfWeekCode | undefined {
  return getGroupTemplateDaysOfWeek(svc)[0];
}

export function setGroupTemplateDayOfWeek(
  svc: HealthcareService,
  dayOfWeek: DayOfWeekCode | string | undefined,
): HealthcareService {
  const code = parseDayOfWeekCode(dayOfWeek);
  return setGroupTemplateDaysOfWeek(svc, code ? [code] : undefined);
}

export function getGroupTemplateEnrollmentMode(
  svc: HealthcareService,
): GroupEnrollmentMode | undefined {
  for (const cc of svc.characteristic ?? []) {
    const code = cc.coding?.find((c) => c.system === GROUP_ENROLLMENT_MODE_SYSTEM)?.code;
    const mode = asEnrollmentMode(code);
    if (mode) return mode;
  }
  return undefined;
}

export function setGroupTemplateEnrollmentMode(
  svc: HealthcareService,
  mode: GroupEnrollmentMode | undefined,
): HealthcareService {
  const others = (svc.characteristic ?? []).filter((cc) => !hasEnrollmentModeCoding(cc));
  const characteristic = mode
    ? [
        ...others,
        {
          coding: [
            {
              system: GROUP_ENROLLMENT_MODE_SYSTEM,
              code: mode,
              display: mode === 'open' ? 'Open' : 'Closed',
            },
          ],
        },
      ]
    : others;
  return { ...svc, characteristic: characteristic.length ? characteristic : undefined };
}

export function getGroupTemplateCapacity(svc: HealthcareService): GroupTemplateCapacity {
  const cap = svc.extension?.find((e) => e.url === GROUP_TEMPLATE_CAPACITY_EXT);
  const int = (url: string) =>
    subExt(cap?.extension, url)?.valueUnsignedInt ?? subExt(cap?.extension, url)?.valueInteger;
  return { minEnrollment: int('minEnrollment'), maxEnrollment: int('maxEnrollment') };
}

export function setGroupTemplateCapacity(
  svc: HealthcareService,
  capacity: GroupTemplateCapacity,
): HealthcareService {
  const sub: Extension[] = [];
  if (capacity.minEnrollment != null) {
    sub.push({ url: 'minEnrollment', valueUnsignedInt: capacity.minEnrollment });
  }
  if (capacity.maxEnrollment != null) {
    sub.push({ url: 'maxEnrollment', valueUnsignedInt: capacity.maxEnrollment });
  }
  return replaceExtension(
    svc,
    GROUP_TEMPLATE_CAPACITY_EXT,
    sub.length ? { url: GROUP_TEMPLATE_CAPACITY_EXT, extension: sub } : undefined,
  );
}

export function getGroupTemplateModality(svc: HealthcareService): GroupSessionModality | undefined {
  for (const cc of svc.serviceProvisionCode ?? []) {
    const code =
      cc.coding?.find((c) => c.system === SESSION_MODALITY_SYSTEM)?.code
      ?? cc.coding?.[0]?.code;
    const modality = asGroupSessionModality(code);
    if (modality) return modality;
  }
  return undefined;
}

export function setGroupTemplateModality(
  svc: HealthcareService,
  modality: GroupSessionModality | undefined,
): HealthcareService {
  const others = (svc.serviceProvisionCode ?? []).filter((cc) => !hasModalityCoding(cc));
  const serviceProvisionCode = modality
    ? [...others, { coding: [{ system: SESSION_MODALITY_SYSTEM, code: modality }] }]
    : others;
  return {
    ...svc,
    serviceProvisionCode: serviceProvisionCode.length ? serviceProvisionCode : undefined,
  };
}

export function getGroupTemplateTimezone(svc: HealthcareService): string | undefined {
  const ext = svc.extension?.find((e) => e.url === GROUP_TEMPLATE_TIMEZONE_EXT);
  return ext?.valueCode ?? ext?.valueString;
}

export function setGroupTemplateTimezone(
  svc: HealthcareService,
  timezone: string | undefined,
): HealthcareService {
  const tz = timezone?.trim();
  return replaceExtension(
    svc,
    GROUP_TEMPLATE_TIMEZONE_EXT,
    tz ? { url: GROUP_TEMPLATE_TIMEZONE_EXT, valueCode: tz } : undefined,
  );
}

export const getGroupTemplatePhoto = (svc: HealthcareService) => svc.photo;
export const getGroupTemplatePhotoUrl = (svc: HealthcareService) => svc.photo?.url;
export const setGroupTemplatePhoto = (
  svc: HealthcareService,
  photo: Attachment | undefined,
): HealthcareService => ({ ...svc, photo });

export const getGroupTemplateDescription = (svc: HealthcareService) =>
  svc.comment?.trim() || undefined;
export const setGroupTemplateDescription = (
  svc: HealthcareService,
  description: string | undefined,
): HealthcareService => ({ ...svc, comment: description?.trim() || undefined });

export function healthcareServiceRef(id: string, display?: string): Reference {
  return { reference: `HealthcareService/${id}`, ...(display ? { display } : {}) };
}
